import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { accentBg } from "../../utils/accent";
import {
  Menu,
  LogOut,
  User,
  Rocket,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronRight,
  Shield,
  ArrowLeftRight,
  Compass,
  Phone,
  MessageCircle,
  Mail,
  UserRound,
} from "lucide-react";
import api from "../../utils/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import WSStatusBadge from "../WSStatusBadge";
import HeaderChatButton from "../HeaderChatButton";
import WhatsNewButton from "../changelog/WhatsNewButton";
import NotificationBell from "../notifications/NotificationBell";
import ProfileDrawer from "./ProfileDrawer";
import { navItems } from "./Sidebar";
import { getBreadcrumb, subscribeBreadcrumb } from "../../utils/breadcrumb";
import AjudaEmVideo from "../AjudaEmVideo";

// Percorre navItems e retorna a lista de labels do caminho até a rota atual.
// Ex.: /settings/pauses/general → ["Configurações", "Mobile", "Pausas", "Geral"]
function findBreadcrumb(nodes, path, trail = []) {
  for (const n of nodes || []) {
    if (n.path === path) return [...trail, n.label];
    if (n.children?.length) {
      const found = findBreadcrumb(n.children, path, [...trail, n.label]);
      if (found) return found;
    }
  }
  return null;
}

const routeNames = {
  "/dashboard": "Dashboard",
  "/whats-new": "Novidades",

  // Serviços
  "/services/list": "Serviços",
  "/services/logistics": "Painel Logístico",
  "/wallboard": "Wallboard",
  "/services/commissions": "Remunerações",
  "/assessment": "Fechamento",
  "/fiscal": "Fiscal",
  "/fiscal/nfse": "NFS-e",
  "/fiscal/profiles": "Perfis Fiscais",
  "/reports/productivity": "Produtividade",
  "/reports/productivity/users": "Produtividade · Usuários",

  // Financeiro
  "/finance/entries": "Lançamentos",
  "/finance/statement": "Extrato",
  "/finance/ofx-imports": "Importações OFX",

  // Configurações (Settings)
  "/settings/people": "Pessoas",
  "/settings/pois": "Pontos de Interesse",
  "/settings/branches": "Filiais",
  "/settings/vehicles": "Viaturas",
  "/settings/service-types": "Tipos de Serviços",
  "/settings/tags": "Tags e SuperTags",
  "/settings/users": "Usuários",
  "/settings/custom-fields": "Campos Personalizados",
  "/settings/quick-messages": "Mensagens Rápidas",
  "/settings/fuel-types": "Combustíveis",
  "/settings/cancel-reasons": "Motivos de Cancelamento",
  "/settings/rejection-reasons": "Motivos de Rejeição",
  "/settings/trackers": "Rastreadores",
  "/settings/automations": "Automações",
  "/settings/branding": "Marca",
  "/settings/speed-control": "Controle de Velocidade",
  "/settings/dispatch-expiration": "Expiração de Despacho",
  "/settings/dispatch-routing": "Roteamento de Despacho",
  "/settings/logs": "Logs de Auditoria",
  "/audit/logs": "Logs",
  "/services/closing": "Fechamento",
  "/audit/access": "Acessos",
  "/audit/sessions": "Sessões",
  "/audit/alerts": "Alertas de Segurança",
  "/settings/notifications": "Notificações",

  // Pausas
  "/settings/pauses": "Pausas",
  "/settings/pauses/general": "Pausas — Configurações",
  "/settings/pauses/reasons": "Pausas — Motivos",
  "/settings/pauses/history": "Pausas — Histórico",

  // Financeiro (Configurações)
  "/settings/finance/general": "Financeiro — Geral",
  "/settings/finance/categories": "Plano de Contas",
  "/settings/finance/bank-accounts": "Contas Bancárias",
  "/settings/finance/payment-methods": "Formas de Pagamento",
  "/settings/finance/cost-centers": "Centros de Custo",

  // Configurações
  "/settings/general": "Configurações Gerais",
  "/fleet/maintenance": "Manutenções",
  "/fleet/maintenance/plans": "Planos Preventivos",
  "/fleet/fuel": "Abastecimentos",

  // Admin (super_admin)
  "/admin": "Admin — Dashboard",
  "/admin/clients": "Admin — Clientes",
  "/admin/plans": "Admin — Planos",
  "/admin/finance": "Admin — Financeiro",
  "/admin/audit": "Admin — Auditoria",
  "/admin/captures": "Admin — Capturas",
  "/admin/users": "Admin — Usuários",
  "/admin/integrations/positron": "Admin — Positron",
};

export default function Header({ onMenuClick, sidebarCollapsed, onToggleSidebar, items, adminMode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [gerente, setGerente] = useState(null);
  const [gerenteBuscado, setGerenteBuscado] = useState(false);

  /** Gerente da carteira do cliente — buscado só quando o menu abre. */
  const carregarGerente = () => {
    if (gerenteBuscado) return;
    setGerenteBuscado(true);
    api.get("/me/account-manager")
      .then((r) => setGerente(r.data?.gerente || null))
      .catch(() => {});
  };
  const [override, setOverride] = useState(getBreadcrumb);
  useEffect(() => subscribeBreadcrumb(setOverride), []);

  const trail = override?.path === location.pathname
    ? override.trail
    : /^\/panels\/[^/]+$/.test(location.pathname)
      ? ["Painéis", "Editar painel"]
      : findBreadcrumb(items || navItems, location.pathname) || [routeNames[location.pathname] || "Dashboard"];
  const [copiado, setCopiado] = useState(false);
  const copiarTrilha = async () => {
    const texto = `${trail.join(" > ")} (${location.pathname})`;
    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      const area = document.createElement("textarea");
      area.value = texto;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1600);
  };

  const soDigitos = (v) => (v || "").replace(/\D/g, "");
  const telefoneGerente = soDigitos(gerente?.telefone);
  const whatsGerente = telefoneGerente.length >= 10 ? `55${telefoneGerente}` : "";

  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between bg-white border-b border-gray-200 px-4 py-3 md:px-6 transition-colors"
      data-testid="header"
    >
      {/* Left: hamburger + breadcrumbs */}
      <div className="flex items-center gap-4">
        <button
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          onClick={onMenuClick}
          data-testid="header-menu-btn"
        >
          <Menu size={22} className="text-gray-600" />
        </button>

        {/* Breadcrumbs completos — clique copia "Trilha (/rota)" */}
        <nav
          className="hidden md:flex items-center gap-1.5 text-sm min-w-0"
          data-testid="breadcrumbs"
        >
          <button
            type="button"
            onClick={onToggleSidebar}
            className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            title={sidebarCollapsed ? "Expandir menu" : "Recolher menu"}
            data-testid="sidebar-toggle-btn"
          >
            {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
          {trail.map((label, idx) => (
            <span key={idx} className="flex items-center gap-1.5 min-w-0">
              <ChevronRight size={14} className="text-gray-300 shrink-0" />
              <button
                type="button"
                onClick={copiarTrilha}
                title={`Copiar "${trail.join(" > ")} (${location.pathname})"`}
                className={`truncate transition-colors hover:text-gray-900 ${
                  idx === trail.length - 1
                    ? "font-medium text-gray-700"
                    : "text-gray-500"
                }`}
                data-testid={`breadcrumb-item-${idx}`}
              >
                {label}
              </button>
            </span>
          ))}
          {/* Vídeo de ajuda da tela, à direita do breadcrumb */}
          <AjudaEmVideo />
          {copiado && (
            <span
              className="ml-1 shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700"
              data-testid="breadcrumb-copied"
            >
              copiado
            </span>
          )}
        </nav>
      </div>

      {/* Right: no /admin só "Voltar ao tenant" e "Sair" */}
      {adminMode ? (
        <div className="flex items-center gap-2 ml-auto" data-testid="header-admin-actions">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            data-testid="header-back-to-tenant-btn"
          >
            <ArrowLeftRight size={16} />
            <span className="hidden sm:inline">Voltar ao cliente</span>
          </Link>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-red-200 bg-white text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            data-testid="header-logout-btn"
          >
            <LogOut size={16} />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      ) : (
      <div className="flex items-center gap-1.5 ml-auto">
        {/* WebSocket Status */}
        <div className="hidden sm:flex items-center mr-1">
          <WSStatusBadge />
        </div>

        {/* Admin shortcut (super_admin only), mesmo estilo do bell */}
        {user?.role === "super_admin" && (
          <Link
            to="/admin"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="header-admin-link"
            title="Painel administrativo"
          >
            <Shield size={20} className="text-gray-500" />
          </Link>
        )}

        {/* Novidades do sistema */}
        <WhatsNewButton />

        {/* Assistente de configuração inicial — disponível para qualquer usuário */}
        <button
          type="button"
          onClick={() => navigate("/onboarding")}
          className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Assistente de configuração"
          data-testid="header-onboarding-btn"
        >
          <Compass size={20} className="text-gray-500" />
        </button>


        {/* Chat / Mensagens */}
        <HeaderChatButton />

        {/* Notifications */}
        <NotificationBell />

        {/* User Menu */}
        <DropdownMenu onOpenChange={(aberto) => { if (aberto) carregarGerente(); }}>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              data-testid="header-user-menu-btn"
            >
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={accentBg()}>
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-gray-800 leading-tight">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-400 leading-tight max-w-[180px] truncate">
                  {user?.email}
                </p>
              </div>
              <ChevronDown
                size={16}
                className="text-gray-400 hidden sm:block"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            {gerente && (
              <>
                <div className="px-3 py-2.5" data-testid="header-account-manager">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Seu gerente de conta
                  </p>
                  <div className="mt-2 flex items-center gap-2.5">
                    {gerente.foto_url ? (
                      <img
                        src={gerente.foto_url}
                        alt={gerente.nome}
                        className="h-10 w-10 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <span className="h-10 w-10 rounded-full bg-gray-100 text-gray-400 inline-flex items-center justify-center border border-gray-200">
                        <UserRound size={20} />
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate" data-testid="account-manager-name">
                        {gerente.nome}
                      </p>
                      {gerente.funcao && (
                        <p className="text-xs text-gray-500 truncate">{gerente.funcao}</p>
                      )}
                    </div>
                  </div>

                  {telefoneGerente && (
                    <div className="mt-2.5 flex items-center gap-1.5">
                      <a
                        href={`tel:+${whatsGerente || telefoneGerente}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md border border-gray-200 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                        data-testid="account-manager-call"
                      >
                        <Phone size={12} /> Ligar
                      </a>
                      {whatsGerente && (
                        <a
                          href={`https://wa.me/${whatsGerente}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md border border-emerald-200 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
                          data-testid="account-manager-whatsapp"
                        >
                          <MessageCircle size={12} /> WhatsApp
                        </a>
                      )}
                    </div>
                  )}

                  {gerente.email && (
                    <a
                      href={`mailto:${gerente.email}`}
                      className="mt-1.5 inline-flex w-full items-center justify-center gap-1.5 h-7 rounded-md border border-gray-200 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                      data-testid="account-manager-email"
                    >
                      <Mail size={12} /> Enviar e-mail
                    </a>
                  )}
                </div>
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => setProfileOpen(true)}
              data-testid="header-profile-btn"
            >
              <User size={16} className="mr-2" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => navigate("/whats-new")}
              data-testid="header-whats-new-menu"
            >
              <Rocket size={16} className="mr-2" />
              Novidades
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="cursor-pointer text-red-600 focus:text-red-600"
              data-testid="header-logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      )}

      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  );
}
